import { Component, OnInit, Inject, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LanguageService } from '../../shared/services/language.service';
import { ProfileService } from '../../shared/services/profile.service';
import { Profile } from 'src/app/models/Profile';
import { InfoBarWithStatusService } from '../../shared/services/info-bar-with-status.service';
import { forkJoin } from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-perfiles-dialog',
    templateUrl: './perfiles-dialog.component.html',
    styleUrls: ['./perfiles-dialog.component.css']
})
export class PerfilesDialogComponent implements OnInit {

    variables: any;
    local_data: any;
    formGroup: FormGroup;
    isEdit = false;

    nameInvalidInput = false;
    nameErrorMessage = '';
    costInvalidInput = false;
    costErrorMessage = '';
    profileNames: any;
    centers: any;

    otherNameType = false;
    otherCenterType = false;

    constructor(
        public dialogRef: MatDialogRef<PerfilesDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
        private _formBuilder: FormBuilder,
        private profileService: ProfileService,
        private infoBar: InfoBarWithStatusService,
        private languageService: LanguageService
    ) {
        this.local_data = { ...data };
        this.formGroup = this._formBuilder.group({
            standardNameCtrl: [{ value: '' }, Validators.required],
            nameCtrl: ['', Validators.required],
            descriptionCtrl: [''],
            costCtrl: ['', [Validators.required, Validators.min(0)]],
            standardCenterCtrl: [{ value: '' }],
            centerCtrl: ['']
        });
    }

    ngOnInit(): void {
        this.languageService.translations$.subscribe((vars: any) => {
            this.variables = vars;
        });

        this.isEdit = this.local_data.isEdit;

        this.loadProfiles();

        if (this.isEdit && this.local_data.profile) {
            this.formGroup.get('nameCtrl')?.setValue(this.local_data.profile.name);
            this.formGroup.get('descriptionCtrl')?.setValue(this.local_data.profile.description);
            this.formGroup.get('costCtrl')?.setValue(this.local_data.profile.costPerHour);
            this.formGroup.get('centerCtrl')?.setValue(this.local_data.profile.center || '');
        }

    }


    loadProfiles(): void {
        forkJoin({
            profileNames: this.profileService.getAllProfileNames(),
            centers: this.profileService.getAllCenters()
        }).subscribe({
            next: (result: { profileNames: string[]; centers: string[]; }) => {
                this.profileNames = result.profileNames;
                this.centers = result.centers;

                console.log(this.profileNames);
                console.log(this.centers);


                // Check if editing and value is not in standard lists
                if (this.isEdit && this.local_data.profile) {
                    const profileName = this.local_data.profile.name;
                    if (profileName && !this.profileNames.includes(profileName)) {
                        this.otherNameType = true;
                        this.formGroup.get('standardNameCtrl')?.setValue('other');
                    } else if (profileName) {
                        this.formGroup.get('standardNameCtrl')?.setValue(profileName);
                    }

                    const center = this.local_data.profile.center;
                    if (center && !this.centers.includes(center)) {
                        this.otherCenterType = true;
                        this.formGroup.get('standardCenterCtrl')?.setValue('other');
                    } else if (center) {
                        this.formGroup.get('standardCenterCtrl')?.setValue(center);
                    }
                }
            },
            error: (err) => {
                console.log(err);
                this.infoBar.showSnackbar(this.variables?.errorLoadingProfiles || 'Error loading profiles', ['error-snackbar'], 3000);
            }
        });
    }

    onProfileNameChange(value: string): void {
        this.otherNameType = value === 'other';
        if (!this.otherNameType) {
            this.formGroup.get('nameCtrl')?.setValue(value);
        } else {
            this.formGroup.get('nameCtrl')?.setValue('');
        }
    }

    onCenterChange(value: string): void {
        this.otherCenterType = value === 'other';
        if (!this.otherCenterType) {
            this.formGroup.get('centerCtrl')?.setValue(value);
        } else {
            this.formGroup.get('centerCtrl')?.setValue('');
        }
    }
    computeKPIs() {
        throw new Error('Method not implemented.');
    }



    private getErrorMessage(err: any, fallbackKey: string): string {
        const serverMessage = err?.error?.message || err?.error;

        // Map server error messages to translation keys
        if (typeof serverMessage === 'string') {
            if (serverMessage.toLowerCase().includes('profile with this name already exists')) {
                return this.variables?.profileNameAlreadyExists || serverMessage;
            }
        }

        // Fallback to generic error message
        return this.variables?.[fallbackKey] || serverMessage || 'An error occurred';
    }

    doAction(): void {
        this.nameInvalidInput = false;
        this.costInvalidInput = false;

        const formValue = this.formGroup.value;
        let valid = true;

        if (!formValue.nameCtrl || formValue.nameCtrl.trim() === '') {
            this.nameInvalidInput = true;
            this.nameErrorMessage = this.variables?.insertName || 'Ingrese un nombre';
            valid = false;
        }

        if (formValue.costCtrl === null || formValue.costCtrl === '' || formValue.costCtrl < 0) {
            this.costInvalidInput = true;
            this.costErrorMessage = this.variables?.invalidCost || 'Ingrese un coste válido (≥ 0)';
            valid = false;
        }

        if (valid) {

            const profile: Profile = {
                name: formValue.nameCtrl.trim(),
                description: (formValue.descriptionCtrl || '').trim(),
                costPerHour: formValue.costCtrl,
                center: (formValue.centerCtrl || '').trim()
            };

            if (!this.isEdit) {
                this.profileService.addProfile(profile).subscribe({
                    next: () => {
                        this.infoBar.showSnackbar(this.variables?.profileAddedSuccessfully || 'Profile added', ['successful-snackbar'], 2000);

                        this.dialogRef.close({
                            data: {
                                success: true
                            }
                        });
                    },
                    error: (err) => {
                        console.log(err);
                        const msg = this.getErrorMessage(err, 'errorAddingProfile');
                        this.infoBar.showSnackbar(msg, ['error-snackbar'], 3000);
                    }
                });
            }
            else {
                console.log(profile);

                this.profileService.updateProfile(this.local_data.profile.id!, profile).subscribe({
                    next: () => {
                        this.infoBar.showSnackbar(this.variables?.profileEditedSuccessfully || 'Profile updated', ['successful-snackbar'], 2000);

                        this.dialogRef.close({
                            data: {
                                success: true
                            }
                        });
                    },
                    error: (err) => {
                        const msg = this.getErrorMessage(err, 'errorEditingProfile');
                        this.infoBar.showSnackbar(msg, ['error-snackbar'], 3000);
                    }
                });
            }
        }
    }
}
